import { Loading } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { fetchExpenditureBudgetPurpose } from '@/features/master-table/utils';
import { useCreateProject } from '@/features/project/_hooks/project';
import { useCreateSubProject } from '@/features/project/_hooks/sub-project';
import { useCreateExpenditureCart } from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-cart';
import { ProjectForm } from '@/features/project/projects/forms/_components/ProjectForm';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const CreateProjectPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();
  const me = useMe();
  const currentFiscalYear = useCurrentFiscalYear();
  const organizationIds = useRecursiveOrganizationFilteredByKey({
    params: { ids: me.data?.organizationIds ?? [] },
    enabled: !!me.data,
    options: { separator: undefined, filterBy: 'id', reverse: true },
  });

  const { mutateAsync } = useCreateProject();
  const { mutateAsync: createSubProjectAsync } = useCreateSubProject();
  const { mutateAsync: createExpenditureCartAsync } =
    useCreateExpenditureCart();

  if (organizationIds.isPending || currentFiscalYear.isPending) {
    return <Loading />;
  }

  return (
    <ProjectForm
      type="new"
      initialValues={{
        sheetType: 'RS',
        data: {
          fiscalYear: dayjs().year(
            currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
          ),
          nestedRelatedOrganizationIds: organizationIds.data,
        },
      }}
      onSubmit={async (values) => {
        const res = await mutateAsync(values);
        if (
          values.sheetType === 'RS' &&
          values.data.skipAdministrativeReformTeamEvaluation
        ) {
          const majorExpenditureBudgetPurposeIds =
            values.data.majorExpenditureBudgetPurposeIds;
          const expenditureBudgetPurposesId =
            majorExpenditureBudgetPurposeIds?.at(-1) ?? null;

          if (expenditureBudgetPurposesId) {
            const subProjectRes = await createSubProjectAsync({
              projectId: res.id,
              name: '-',
            });

            const { accountTitleId } = await fetchExpenditureBudgetPurpose(
              expenditureBudgetPurposesId,
            );

            await createExpenditureCartAsync({
              accountingId: majorExpenditureBudgetPurposeIds?.[0] ?? '',
              accountTitleId,
              expenditureBudgetPurposesId,
              projectId: res.id,
              subProjectId: subProjectRes.id,
            });
          }
        }
        await Promise.all([
          openMessage('success', t('message-create-completed')),
          (async () => {
            navigate(`/rs/project/${res.id}`, { replace: true });
          })(),
        ]);
      }}
    />
  );
};
